import type {
  DemoBootstrap,
  DemoCourse,
  DemoEvent,
  DemoEventFields,
  ProgressEntry,
  SittingResponse,
  EngagementEventInput,
} from '@cusp/contracts';
import { setTransientIdentity } from '../identity.js';
import { queryClient } from '../queryClient.js';
import { getApiBaseUrl } from '../config.js';
import { useSyncExternalStore } from 'react';
export { isRealTutorPath } from './tutor-paths.js';

const ACTIVE = 'cusp.demo.active';
const PREFIX = 'cusp.demo.v1.';
export const DEMO_USER_ID = '00000000-0000-4000-8000-000000000010';
export const DEMO_TENANT_ID = '00000000-0000-4000-8000-000000000020';
export type Perspective = DemoEvent['perspective'];
export interface DemoState {
  version: 1;
  generation: string;
  contentVersion: string;
  browserId: string;
  progress: Record<string, ProgressEntry>;
  watchedMs: Record<string, number>;
  sittings: Record<string, SittingResponse>;
  drafts: Record<string, string>;
  admin: Record<string, unknown>;
}
let context:
  | { id: string; token?: string; data: DemoBootstrap; state: DemoState; perspective: Perspective }
  | undefined;
let realFetch: typeof fetch;
let inFlight: Promise<void> | undefined;
let resetting = false;
let writer = '';
let session = '';
let cleanup: Array<() => void> = [];
let storageError: string | null = null;
export function useDemoStorageError(): string | null {
  return useSyncExternalStore(
    subscribeDemo,
    () => storageError,
    () => null,
  );
}
export function disposeDemo(): void {
  for (const fn of cleanup) fn();
  cleanup = [];
  if (context) window.fetch = realFetch;
  context = undefined;
  resetting = false;
  storageError = null;
  setTransientIdentity(undefined);
  listeners.clear();
}
function listen(type: string, listener: (event: Event) => void): void {
  window.addEventListener(type, listener);
  cleanup.push(() => window.removeEventListener(type, listener));
}
const listeners = new Set<() => void>();
const VISIT_IDLE_MS = 30 * 60 * 1000;
let lastOccurred = 0;
/**
 * Strictly increasing within a page, so events recorded in one gesture (a
 * graded sitting's answers, then its submission) keep their order in the
 * timeline instead of tying on the millisecond and sorting by random id.
 */
function occurredAt(): string {
  lastOccurred = Math.max(Date.now(), lastOccurred + 1);
  return new Date(lastOccurred).toISOString();
}
const apiPath = (path: string) => `${getApiBaseUrl()}${path}`;
export function demoActive(): boolean {
  return context !== undefined;
}
export function demoData(): DemoBootstrap {
  if (!context) throw new Error('Demo is not initialized');
  return context.data;
}
export function demoLearnerName(): string {
  return demoData().learnerName?.trim() || 'Alex Morgan';
}
export function demoState(): DemoState {
  if (!context) throw new Error('Demo is not initialized');
  return context.state;
}
export function demoKey(): string {
  return context?.id ?? '';
}
export function demoPerspective(): Perspective {
  return context?.perspective ?? 'chooser';
}
export function subscribeDemo(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function read<T>(key: string, fallback: T): T {
  try {
    return (JSON.parse(localStorage.getItem(key) ?? 'null') as T) ?? fallback;
  } catch {
    return fallback;
  }
}
function stateKey() {
  return `${PREFIX}${context!.id}.state`;
}
function fresh(contentVersion: string, browserId: string = crypto.randomUUID()): DemoState {
  return {
    version: 1,
    generation: crypto.randomUUID(),
    contentVersion,
    browserId,
    progress: {},
    watchedMs: {},
    sittings: {},
    drafts: {},
    admin: {},
  };
}
function validState(saved: DemoState | null, contentVersion: string): saved is DemoState {
  return Boolean(
    saved &&
    saved.version === 1 &&
    typeof saved.generation === 'string' &&
    saved.contentVersion === contentVersion &&
    typeof saved.browserId === 'string' &&
    ['progress', 'sittings', 'drafts', 'admin'].every((key) => {
      const value = saved[key as keyof DemoState];
      return value && typeof value === 'object' && !Array.isArray(value);
    }),
  );
}
export function updateDemo(change: (state: DemoState) => void): void {
  if (!context || resetting) return;
  const saved = storageError ? context.state : read<DemoState>(stateKey(), context.state);
  const next = structuredClone(
    validState(saved, context.data.contentVersion) ? saved : context.state,
  );
  change(next);
  try {
    localStorage.setItem(stateKey(), JSON.stringify(next));
    storageError = null;
  } catch {
    storageError = 'Browser storage is full. New changes will last only until you leave this page.';
  }
  context.state = next;
  refreshWatch();
  for (const fn of listeners) fn();
}
/** Serialize complete read/modify/write actions across learner and admin tabs. */
async function locked<T>(id: string, action: () => T | Promise<T>): Promise<T> {
  return navigator.locks.request(`${PREFIX}${id}.write`, action);
}
export async function demoAction<T>(action: () => T | Promise<T>): Promise<T | undefined> {
  if (!context || resetting) return;
  const current = context;
  return locked(current.id, async () => {
    if (context !== current || resetting) return;
    const saved = read<DemoState | null>(stateKey(), null);
    if (saved && saved.generation !== current.state.generation) {
      resetting = true;
      window.location.assign('/demo');
      return;
    }
    if (!storageError && validState(saved, current.data.contentVersion)) current.state = saved;
    refreshWatch();
    return action();
  });
}
function watchPrefix(): string {
  return `${PREFIX}${context!.id}.watch.${context!.state.generation}.`;
}
function refreshWatch(): void {
  if (!context || storageError) return;
  const prefix = watchPrefix();
  const totals: Record<string, number> = {};
  for (const key of Object.keys(localStorage)) {
    if (!key.startsWith(prefix)) continue;
    const count = read<{ lecture: string; milliseconds: number } | null>(key, null);
    if (count && Number.isFinite(count.milliseconds))
      totals[count.lecture] = (totals[count.lecture] ?? 0) + count.milliseconds;
  }
  context.state.watchedMs = totals;
}
export async function resetDemo(): Promise<void> {
  await demoAction(() => {
    const previousWatch = watchPrefix();
    const browserId = demoState().browserId;
    updateDemo((s) => Object.assign(s, fresh(demoData().contentVersion, browserId)));
    for (const key of Object.keys(localStorage))
      if (key.startsWith(`${PREFIX}${demoKey()}.sitting.`) || key.startsWith(previousWatch))
        localStorage.removeItem(key);
    recordDemo('demo.reset');
    // Closing media/progress writers still flush on navigation. Keep their
    // historical events, but do not let them repopulate the freshly reset state.
    resetting = true;
    queryClient.clear();
    window.location.assign('/demo');
  });
}
/**
 * Leave the demo, recording that the visitor chose to. The event is sent before
 * navigating: a batch already in flight is awaited and a second one sent, all
 * bounded so a slow network never holds the visitor on the page.
 */
export async function exitDemo(): Promise<void> {
  if (context?.token) {
    recordDemo('demo.exit');
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      flushDemo().then(() => flushDemo()),
      new Promise<void>((resolve) => (timer = setTimeout(resolve, 1500))),
    ]);
    clearTimeout(timer);
  }
  sessionStorage.removeItem(ACTIVE);
  window.location.assign('/');
}
/** The attempt number the next graded sitting for this lesson will carry. */
export function nextDemoAttempt(courseSlug: string, lectureKey: string): number {
  return (
    Object.values(demoState().sittings).filter(
      (s) => s.courseSlug === courseSlug && s.lectureKey === lectureKey,
    ).length + 1
  );
}
export function demoDestination(perspective: 'learner' | 'admin'): string {
  return perspective === 'learner'
    ? `/courses?demo=${encodeURIComponent(demoKey())}`
    : `/admin/?demo=${encodeURIComponent(demoKey())}`;
}
export function demoStorageKey(kind: string): string {
  return `${PREFIX}${demoKey()}.${kind}`;
}
/** Persist the observation synchronously, including the last playback sample on pagehide. */
export function recordLearnerDemoEvent(
  e: Pick<EngagementEventInput, 'kind' | 'courseSlug' | 'lectureKey' | 'durationMs'>,
): void {
  const fields = { courseSlug: e.courseSlug, lectureKey: e.lectureKey };
  if (e.kind === 'page_view' && e.courseSlug && !e.lectureKey) recordDemo('course.open', fields);
  if (e.kind === 'lecture_opened') recordDemo('lecture.open', fields);
  const elapsed = Math.min(60000, Math.round(e.durationMs ?? 0));
  if ((e.kind === 'video_progress' || e.kind === 'audio_progress') && elapsed > 0)
    recordDemo('playback.watch', { ...fields, value: elapsed });
}
function visitKey(): string {
  return `${PREFIX}${context!.id}.visit`;
}
/** The visit still open for this link, if activity was recorded within the idle window. */
function openVisit(): string | null {
  const visit = read<{ id: string; at: number } | null>(visitKey(), null);
  return visit && Date.now() - visit.at <= VISIT_IDLE_MS ? visit.id : null;
}
/**
 * Record `kind` at most once per visit for the same `key` — a sighting rather
 * than an action, such as an instructor note shown on a page the visitor
 * reloads. A different key (a revised note) is a new sighting.
 */
export function recordDemoOncePerVisit(
  kind: DemoEvent['kind'],
  key: string,
  fields: DemoEventFields = {},
): void {
  if (!context?.token) return;
  const marker = `${PREFIX}${context.id}.once.${kind}`;
  try {
    const visit = openVisit();
    if (visit && read<string | null>(marker, null) === `${visit}|${key}`) return;
    recordDemo(kind, fields);
    localStorage.setItem(marker, JSON.stringify(`${session}|${key}`));
  } catch {
    // Analytics must never prevent a learner from using the local simulation.
  }
}
export function recordDemo(kind: DemoEvent['kind'], fields: DemoEventFields = {}): void {
  if (context && kind === 'playback.watch' && fields.courseSlug && fields.lectureKey) {
    const saved = read<DemoState | null>(stateKey(), null);
    if (!resetting && saved?.generation === context.state.generation) {
      const lecture = `${fields.courseSlug}/${fields.lectureKey}`;
      try {
        const key = `${watchPrefix()}${writer}.${encodeURIComponent(lecture)}`;
        const milliseconds =
          read<{ milliseconds: number }>(key, { milliseconds: 0 }).milliseconds +
          (fields.value ?? 0);
        // Each page owns its counter: the final pagehide sample is synchronous
        // and concurrent players cannot overwrite each other's watch time.
        localStorage.setItem(key, JSON.stringify({ lecture, milliseconds }));
        refreshWatch();
      } catch {
        context.state.watchedMs[lecture] =
          (context.state.watchedMs[lecture] ?? 0) + (fields.value ?? 0);
        storageError =
          'Browser storage is full. New changes will last only until you leave this page.';
      }
      for (const fn of listeners) fn();
    }
  }
  if (!context?.token) return;
  try {
    const visit = read<{ id: string; at: number } | null>(visitKey(), null);
    const isNew = !visit || Date.now() - visit.at > VISIT_IDLE_MS;
    session = isNew ? nextSession(visit?.id) : visit.id;
    localStorage.setItem(visitKey(), JSON.stringify({ id: session, at: Date.now() }));
    if (isNew && kind !== 'visit') enqueue('visit');
    enqueue(kind, fields);
    const keys = queueKeys();
    for (const key of keys.slice(0, Math.max(0, keys.length - 500))) localStorage.removeItem(key);
  } catch {
    // Analytics must never prevent a learner from using the local simulation.
  }
}
function nextSession(previous?: string): string {
  if (!previous) return crypto.randomUUID();
  // Concurrent resumes after an idle period derive the same next visit.
  const suffix = ((BigInt(`0x${previous.slice(-12)}`) + 1n) % (1n << 48n))
    .toString(16)
    .padStart(12, '0');
  return `${previous.slice(0, -12)}${suffix}`;
}
function enqueue(kind: DemoEvent['kind'], fields: DemoEventFields = {}): void {
  const event: DemoEvent = {
    id: crypto.randomUUID(),
    browserId: context!.state.browserId,
    sessionId: session,
    kind,
    perspective: context!.perspective,
    occurredAt: occurredAt(),
    ...fields,
  };
  // One storage key per event prevents two tabs overwriting each other's queue.
  localStorage.setItem(`${PREFIX}${context!.id}.event.${event.id}`, JSON.stringify(event));
}
function queueKeys(): string[] {
  return Object.keys(localStorage)
    .filter((k) => k.startsWith(`${PREFIX}${context!.id}.event.`))
    .sort(
      (a, b) =>
        (read<DemoEvent | null>(a, null)?.occurredAt ?? '').localeCompare(
          read<DemoEvent | null>(b, null)?.occurredAt ?? '',
        ) || a.localeCompare(b),
    );
}
/** Send the oldest queued batch. A call made while one is travelling shares it. */
export function flushDemo(): Promise<void> {
  if (!context?.token) return Promise.resolve();
  inFlight ??= sendBatch(context.token).finally(() => {
    inFlight = undefined;
  });
  return inFlight;
}
async function sendBatch(token: string): Promise<void> {
  let keys: string[] = [];
  try {
    keys = queueKeys().slice(0, 50);
    if (!keys.length) return;
    const res = await realFetch(apiPath('/v1/demo/events'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-demo-token': token },
      credentials: 'omit',
      keepalive: true,
      body: JSON.stringify({
        events: keys.map((k) => read<DemoEvent | null>(k, null)).filter(Boolean),
      }),
    });
    if (
      res.ok ||
      (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429)
    )
      for (const k of keys) localStorage.removeItem(k);
  } catch {
    /* Retry the same event IDs on the next interval or visit. */
  }
}
export async function demoFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  for (const name of ['authorization', 'x-user-id', 'x-tenant-id', 'x-role']) headers.delete(name);
  if (context?.token) headers.set('x-demo-token', context.token);
  return realFetch(apiPath(path), { ...init, headers, credentials: 'omit' });
}
export function shouldBootDemo(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '');
  return (
    ['/demo', '/internal-demo', '/admin/internal-demo'].includes(path) ||
    new URLSearchParams(location.search).has('demo') ||
    ((path.startsWith('/courses') || path.startsWith('/admin')) &&
      Boolean(sessionStorage.getItem(ACTIVE)))
  );
}
export async function bootDemo(perspective: Perspective): Promise<void> {
  if (!navigator.locks)
    throw new Error(
      'This demo requires a current browser with secure storage coordination. Open the HTTPS link in an updated browser.',
    );
  // Fail visibly before replacing transport/identity if persistence cannot work.
  localStorage.setItem(`${PREFIX}probe`, '1');
  localStorage.removeItem(`${PREFIX}probe`);
  const url = new URL(location.href);
  let id = url.pathname.endsWith('/internal-demo')
    ? 'internal'
    : (url.searchParams.get('demo') ?? sessionStorage.getItem(ACTIVE) ?? 'internal');
  let token = read<string | undefined>(`${PREFIX}${id}.token`, undefined);
  if (url.pathname === '/demo' && url.hash) {
    token = decodeURIComponent(url.hash.slice(1));
    id = `pending-${crypto.randomUUID()}`;
    // Keep a failed bootstrap retryable after removing the bearer token from the URL.
    localStorage.setItem(`${PREFIX}${id}.token`, JSON.stringify(token));
    sessionStorage.setItem(ACTIVE, id);
    history.replaceState(null, '', url.pathname);
  }
  if (id !== 'internal' && !token)
    throw new Error('Open the original demo link to continue in this browser.');
  realFetch = window.fetch.bind(window);
  const res = await realFetch(apiPath('/v1/demo/bootstrap'), {
    headers: token ? { 'x-demo-token': token } : {},
    credentials: 'omit',
  });
  if (!res.ok)
    throw new Error(
      res.status === 401
        ? 'This demo link is unavailable.'
        : 'The demo could not load. Please try again.',
    );
  const data = (await res.json()) as DemoBootstrap;
  const pendingId = id;
  id = data.linkId ?? 'internal';
  await locked(id, () => {
    if (token) localStorage.setItem(`${PREFIX}${id}.token`, JSON.stringify(token));
    sessionStorage.setItem(ACTIVE, id);
    if (pendingId.startsWith('pending-')) localStorage.removeItem(`${PREFIX}${pendingId}.token`);
    const saved = read<DemoState | null>(`${PREFIX}${id}.state`, null);
    const previousBrowserId = saved?.browserId;
    const state = validState(saved, data.contentVersion)
      ? saved
      : fresh(data.contentVersion, previousBrowserId);
    context = { id, token, data, state, perspective };
    writer = crypto.randomUUID();
    localStorage.setItem(stateKey(), JSON.stringify(state));
    refreshWatch();
    setTransientIdentity({
      userId: perspective === 'admin' ? '00000000-0000-4000-8000-000000000011' : DEMO_USER_ID,
      tenantId: DEMO_TENANT_ID,
      tenantSlug: 'demo-school',
      tenantName: 'Cusp Demo School',
      userName: perspective === 'admin' ? 'Dana Kim' : demoLearnerName(),
      email: perspective === 'admin' ? 'dana@example.test' : 'alex@example.test',
      role: perspective === 'admin' ? 'institution_admin' : 'learner',
    });
    // A reload is not a perspective change: record one only on first entry or
    // an actual switch, remembered per link. A reload after the idle window
    // still opens a new visit.
    const recorded = `${PREFIX}${id}.perspective`;
    if (read<Perspective | null>(recorded, null) !== perspective) {
      recordDemo('perspective');
      try {
        localStorage.setItem(recorded, JSON.stringify(perspective));
      } catch {
        // Storage full: the next boot records the perspective again.
      }
    } else if (!openVisit()) recordDemo('visit');
  });
  listen('storage', (event) => {
    const e = event as StorageEvent;
    if (e.key === stateKey() || e.key?.startsWith(watchPrefix())) {
      const saved = read<DemoState | null>(stateKey(), null);
      if (!validState(saved, context!.data.contentVersion)) return;
      if (saved.generation !== context!.state.generation) {
        resetting = true;
        window.location.assign('/demo');
        return;
      }
      context!.state = saved;
      refreshWatch();
      for (const fn of listeners) fn();
      void queryClient.invalidateQueries();
    }
  });
  listen('online', () => void flushDemo());
  listen('pagehide', () => void flushDemo());
  const timer = setInterval(() => {
    void flushDemo();
  }, 10000);
  cleanup.push(() => clearInterval(timer));
  // Drain a prior page's keepalive retry and record first use promptly.
  void flushDemo();
}
export type DemoHandler = (
  url: URL,
  method: string,
  body: Record<string, unknown>,
) => Promise<unknown | Response>;
export interface DemoTransportOptions {
  forward: (path: string) => boolean;
}

const decodePathSegment = (value: string): string => decodeURIComponent(value);

async function recordForwardedTutorEvent(
  path: string,
  method: string,
  response: Response,
): Promise<void> {
  if (!response.ok || method !== 'POST') return;
  const lecture = path.match(
    /^\/v1\/courses\/([^/]+)\/lectures\/([^/]+)\/tutor\/turns(?:\/([^/]+)\/flag)?$/,
  );
  const sessionTurn = path.match(/^\/v1\/courses\/([^/]+)\/tutor\/sessions\/[^/]+\/turns$/);
  if (lecture?.[3]) {
    recordDemo('tutor.flag', {
      courseSlug: decodePathSegment(lecture[1]!),
      lectureKey: decodePathSegment(lecture[2]!),
    });
    return;
  }
  if (!lecture && !sessionTurn) return;
  try {
    const body = (await response.clone().json()) as {
      turn?: { verdict?: string | null; outcome?: string };
    };
    if (!body.turn) return;
    recordDemo('tutor.message', {
      courseSlug: decodePathSegment((lecture ?? sessionTurn)![1]!),
      ...(lecture ? { lectureKey: decodePathSegment(lecture[2]!) } : {}),
      target: (body.turn.verdict ?? body.turn.outcome) === 'refused' ? 'refused' : 'answered',
    });
  } catch {
    // A successful response with an unexpected body remains the API's response.
  }
}

export function installDemoTransport(
  handler: DemoHandler,
  { forward }: DemoTransportOptions = { forward: () => false },
): void {
  window.fetch = async (input, init) => {
    const request = new Request(
      new URL(
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url,
        location.origin,
      ),
      input instanceof Request ? input : init,
    );
    const url = new URL(request.url);
    const apiBase = new URL(getApiBaseUrl() || '/', location.origin);
    const basePath = apiBase.pathname.replace(/\/+$/, '');
    if (url.origin === apiBase.origin && url.pathname.startsWith(`${basePath}/v1/`)) {
      url.pathname = url.pathname.slice(basePath.length);
    } else if (url.origin !== location.origin || !url.pathname.startsWith('/v1/'))
      return realFetch(input, init);
    // Read the request once: forwarded JSON must be re-sent byte for byte, and
    // simulated handlers receive the same body parsed below.
    const method = request.method;
    let rawBody: string | undefined;
    if (method !== 'GET' && method !== 'HEAD') {
      const raw = await request.text();
      if (raw) rawBody = raw;
    }
    if (forward(url.pathname)) {
      if (!context?.token)
        return new Response(
          JSON.stringify({
            error: {
              code: 'NOT_FOUND',
              message: 'The tutor is available in tracked demos.',
              requestId: 'demo',
            },
          }),
          { status: 404, headers: { 'content-type': 'application/json' } },
        );
      const response = await demoFetch(`${url.pathname}${url.search}`, {
        method,
        body: rawBody,
        headers: {
          // Preserve the caller's media type; a bodyless acknowledgement has
          // none. Inventing JSON here makes Fastify reject that empty POST.
          ...(request.headers.has('content-type')
            ? { 'content-type': request.headers.get('content-type')! }
            : {}),
          'x-demo-browser': demoState().browserId,
        },
        signal: request.signal,
      });
      await recordForwardedTutorEvent(url.pathname, method, response);
      return response;
    }
    // Network access from the simulation is explicit via demoFetch only.
    let body: Record<string, unknown> = {};
    if (rawBody) body = JSON.parse(rawBody) as Record<string, unknown>;
    const answer = await demoAction(() => handler(url, method, body));
    if (answer instanceof Response) return answer;
    if (answer === undefined)
      return new Response(
        JSON.stringify({
          error: {
            code: 'NOT_FOUND',
            message: 'This action is unavailable in the demo.',
            requestId: 'demo',
          },
        }),
        { status: 404, headers: { 'content-type': 'application/json' } },
      );
    return new Response(JSON.stringify(answer), {
      headers: { 'content-type': 'application/json' },
    });
  };
}
export function demoCourse(slug: string): DemoCourse | undefined {
  return context?.data.courses.find((c) => c.course.slug === slug);
}
